import {
  FACE_HORIZONTAL_RATIO,
  GRAZE_FRAME_MS,
  GRAZE_MAX_S,
  GRAZE_MIN_S,
  GRAZE_RADIUS_TILES,
  SPECIES,
  STEP_MAX_TILES,
  STEP_MIN_TILES,
  WALK_FRAME_MS,
  isSpecies,
} from './catalog.js';
import {
  advanceWanderWalk,
  constrainToLeash,
  startWanderWalk,
} from '../wander.js';

/** Skip walk targets closer than this — a sub-step shuffle reads as a twitch. */
const TARGET_MIN_STEP = 0.15;

export class Animal {
  constructor(record, options = {}) {
    if (!isSpecies(record.species)) throw new Error(`Unknown species: ${record.species}`);
    this.id = record.id;
    this.species = record.species;
    this.spawnX = record.spawnX;
    this.spawnY = record.spawnY;
    this.x = Number.isFinite(record.x) ? record.x : record.spawnX;
    this.y = Number.isFinite(record.y) ? record.y : record.spawnY;
    this.facing = record.facing === 'left' ? 'left' : 'right';
    this.state = 'graze';
    this.stateTime = 0;
    this.animTime = 0;
    this.targetX = this.x;
    this.targetY = this.y;
    this.random = options.random ?? Math.random;
    this.grazeDuration = this._rollGrazeDuration();
  }

  static fromJSON(record, options = {}) {
    return new Animal(record, options);
  }

  toJSON() {
    // Centi-tile precision: full doubles bloat every save with 17-digit floats
    // for positions nobody can tell apart on a 16px tile.
    const round = (value) => Math.round(value * 100) / 100;
    return {
      id: this.id,
      species: this.species,
      spawnX: round(this.spawnX),
      spawnY: round(this.spawnY),
      x: round(this.x),
      y: round(this.y),
      facing: this.facing,
    };
  }

  get speed() {
    return SPECIES[this.species].speed;
  }

  update(dt, world) {
    if (!(dt > 0)) return;
    this.animTime += dt;
    if (this.state === 'graze') {
      this.stateTime += dt;
      if (this.stateTime >= this.grazeDuration) this._startWalk(world);
      return;
    }
    advanceWanderWalk(this, world, dt, {
      speed: this.speed,
      faceToward: (dx, dy) => this._faceToward(dx, dy),
      rest: () => this._startGraze(),
      constrain: () => constrainToLeash(this, world, {
        radius: GRAZE_RADIUS_TILES,
        rest: () => this._startGraze(),
      }),
    });
  }

  draw(ctx, origin, tileSize, atlas, atlasMeta) {
    const spec = atlasMeta.species[this.species];
    if (!spec) return;
    const frames = this.state === 'graze' ? spec.graze : spec.walk;
    const ms = this.state === 'graze' ? GRAZE_FRAME_MS : WALK_FRAME_MS;
    const frame = frames[Math.floor((this.animTime * 1000) / ms) % frames.length];
    const width = tileSize * (frame.w / 16);
    const height = tileSize * (frame.h / 16);
    const screenX = Math.round(origin.x + this.x * tileSize - width / 2);
    const screenY = Math.round(origin.y + this.y * tileSize - height);
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    if (this.facing === 'left') {
      ctx.translate(screenX + width, screenY);
      ctx.scale(-1, 1);
      ctx.drawImage(atlas, frame.x, frame.y, frame.w, frame.h, 0, 0, width, height);
    } else {
      ctx.drawImage(atlas, frame.x, frame.y, frame.w, frame.h, screenX, screenY, width, height);
    }
    ctx.restore();
  }

  _rollGrazeDuration() {
    return GRAZE_MIN_S + this.random() * (GRAZE_MAX_S - GRAZE_MIN_S);
  }

  _startGraze() {
    this.state = 'graze';
    this.stateTime = 0;
    this.animTime = 0;
    this.grazeDuration = this._rollGrazeDuration();
  }

  _startWalk(world) {
    if (!startWanderWalk(this, world, {
      radius: GRAZE_RADIUS_TILES,
      stepMin: STEP_MIN_TILES,
      stepMax: STEP_MAX_TILES,
      minDistance: TARGET_MIN_STEP,
      faceToward: (dx, dy) => this._faceToward(dx, dy),
    })) {
      this.grazeDuration = this._rollGrazeDuration();
      this.stateTime = 0;
      return;
    }
    this.animTime = 0;
  }

  _faceToward(dx, dy) {
    const distance = Math.hypot(dx, dy);
    if (distance === 0) return;
    if (Math.abs(dx) / distance < FACE_HORIZONTAL_RATIO) return;
    this.facing = dx < 0 ? 'left' : 'right';
  }
}
